import { iaRa } from 'lib/helpers/utility';
import { flattenInfiniteQueryPages } from 'lib/hooks/useInfiniteFetchData';

import { fetchFilterOptions } from './fetchFilterOptions';

const LABEL_LOOKUP_LIMIT = 100;
const LABEL_LOOKUP_STALE_TIME = 5 * 60 * 1000;

const isMissingLabels = (entry) =>
  (!!entry?.queryFn || !!entry?.queryKey) &&
  iaRa(entry?.values).some((item) => item?.label === undefined);

export const hasMissingLabels = (filters) =>
  Object.values(filters || {}).some((entries) => iaRa(entries).some(isMissingLabels));

const fetchSelectedOptions = async ({ key, entry, navigate, queryClient }) => {
  const values = entry.values.map((item) => item?.value);
  const queryFn =
    entry.queryFn || ((nav, params) => fetchFilterOptions(nav, entry.queryKey, params));
  const params = {
    limit: LABEL_LOOKUP_LIMIT,
    page: 1,
    ...entry.params,
    filters: { searchTerm: '', values }
  };

  const page = await queryClient.fetchQuery({
    queryKey: ['filter-option-labels', entry.queryKey || key, params],
    queryFn: () => queryFn(navigate, params),
    staleTime: LABEL_LOOKUP_STALE_TIME
  });

  return iaRa(
    flattenInfiniteQueryPages({
      pages: [page],
      queryKey: entry.queryKey || '',
      formatData: entry.formatData,
      isOptions: !entry.formatData
    })
  );
};

const hydrateEntry = async ({ key, entry, navigate, queryClient }) => {
  if (!isMissingLabels(entry)) return entry;

  try {
    const options = await fetchSelectedOptions({ key, entry, navigate, queryClient });
    let isAnyLabelFound = false;
    const values = entry.values.map((item) => {
      if (item?.label !== undefined) return item;
      const option = options.find((opt) => String(opt?.value) === String(item?.value));
      if (!option) return item;
      isAnyLabelFound = true;
      return { ...item, ...option };
    });
    return isAnyLabelFound ? { ...entry, values } : entry;
  } catch {
    return entry;
  }
};

export const hydrateFilterLabels = async ({ filters, navigate, queryClient }) => {
  let isAnyEntryHydrated = false;

  const hydrated = await Promise.all(
    Object.entries(filters || {}).map(async ([key, entries]) => {
      if (!Array.isArray(entries)) return [key, entries];

      const hydratedEntries = await Promise.all(
        entries.map((entry) => hydrateEntry({ key, entry, navigate, queryClient }))
      );
      if (hydratedEntries.some((entry, index) => entry !== entries[index])) {
        isAnyEntryHydrated = true;
      }
      return [key, hydratedEntries];
    })
  );

  return isAnyEntryHydrated ? Object.fromEntries(hydrated) : filters;
};
